/**
 * Polyfills :focus-visible for non supporting browsers (Safari < 15.4).
 */
function focusVisiblePolyfill() {
  const navKeys = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Space', 'Escape', 'Home', 'End', 'PageUp', 'PageDown'];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (evt) => {
    if (navKeys.includes(evt.code)) mouseClick = false;
  });

  window.addEventListener('mousedown', () => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('is-focused');
    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('is-focused');
  }, true);
}

// Add polyfill if :focus-visible is not supported.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

theme.fetchCache = {
  MAX_CACHE_SIZE: 20,
  cache: new Map(),
  pending: new Map(),

  /**
   * Fetches a URL and returns text, storing the result in a cache used for subsequent requests
   * to the same URL. Use theme.fetchCache.fetch(url) in theme code to return a Promise.
   * @param {Element} url - URL to fetch.
   * @returns {string} Text from request.
   */
  async fetchAndCache(url) {
    try {
      const response = await fetch(url);
      const text = await response.text();

      // Evict oldest if needed
      if (this.cache.size >= this.MAX_CACHE_SIZE) {
        const oldestKey = [...this.cache.entries()]
          .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
        this.cache.delete(oldestKey);
      }

      this.cache.set(url, { text, timestamp: Date.now() });
      return text;
    } finally {
      // Une requête rejetée doit pouvoir être retentée. Sans ce finally, sa
      // Promise rejetée restait dans pending jusqu'au prochain clear global.
      this.pending.delete(url);
    }
  },

  /**
   * Fetches a URL and returns text, using a local cache.
   * @param {Element} url - URL to fetch.
   * @returns {Promise} Promise that completes and returns the response as a string parameter.
   */
  fetch(url) {
    if (this.cache.has(url)) {
      return Promise.resolve(this.cache.get(url).text);
    }

    if (this.pending.has(url)) {
      return this.pending.get(url);
    }

    const promise = this.fetchAndCache(url);
    this.pending.set(url, promise);
    return promise;
  },

  /**
   * Preload a URL in the fetch cache.
   * @param {Element} url - URL to preload.
   */
  preload(url) {
    this.fetch(url).catch(() => {
      this.cache.delete(url);
      this.pending.delete(url);
    });
  },

  /**
   * Clear the cache.
   */
  clear() {
    this.cache.clear();
  }
};

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  /**
   * Handles a media query (breakpoint) change.
   */
  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});

    // Update mediaMatches values after breakpoint change.
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });

    window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
  };

  mqKeys.forEach((mq) => {
    // Create mqList object for each media query.
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);

    // Get initial matches for each query.
    theme.mediaMatches[mq] = mqLists[mq].matches;

    // Add an event listener to each query.
    try {
      mqLists[mq].addEventListener('change', handleMqChange);
    } catch (err1) {
      // Fallback for legacy browsers (Safari < 14).
      mqLists[mq].addListener(handleMqChange);
    }
  });
})();

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  let height = header.offsetHeight;

  // Add announcement bar height (if shown).
  const announcement = document.getElementById('shopify-section-announcement');
  if (announcement) height += announcement.offsetHeight;

  document.documentElement.style.setProperty('--header-height', `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
  document.documentElement.style.setProperty(
    '--scrollbar-width',
    `${window.innerWidth - document.documentElement.clientWidth}px`
  );
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
  setViewportHeight();
  setHeaderHeight();
  setScrollbarWidth();
}

// Set the dimension variables once the DOM is loaded
document.addEventListener('DOMContentLoaded', setDimensionVariables);

// Update the dimension variables if viewport resized.
window.addEventListener('resize', debounce(setDimensionVariables, 400));

// iOS alters screen width without resize event, if unexpectedly wide content is found
setTimeout(setViewportHeight, 3000);

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 * @returns {IntersectionObserver|null} Observer instance, or null when initialised immediately.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof callback === 'function') {
            callback();
            observer.unobserve(entry.target);
          }
        }
      });
    }, { rootMargin: `0px 0px ${threshold}px 0px` });

    io.observe(element);
    return io;
  } else {
    callback();
    return null;
  }
}

let overlayScrollbarsPromise;

/**
 * Loads the optional scrollbar vendor only when a slider is close enough to
 * need it. Native horizontal scrolling remains available if loading fails.
 * @returns {Promise<object|null>} OverlayScrollbars global namespace.
 */
function loadOverlayScrollbars() {
  if (overlayScrollbarsPromise) return overlayScrollbarsPromise;

  const scriptSrc = window.theme?.assets?.overlayScrollbars;
  const styleSrc = window.theme?.assets?.overlayScrollbarsStyles;
  if (!scriptSrc && !window.OverlayScrollbarsGlobal) return Promise.resolve(null);

  const loadStylesheet = (src) => new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }

    const normalisedSrc = new URL(src, document.baseURI).href;
    let link = Array.from(document.querySelectorAll('link[rel~="stylesheet"]'))
      .find((item) => item.href === normalisedSrc);
    if (link?.sheet) {
      resolve();
      return;
    }

    const complete = () => resolve();
    let shouldAppend = false;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = normalisedSrc;
      link.dataset.dgOptionalAsset = 'overlayscrollbars';
      shouldAppend = true;
    }
    link.addEventListener('load', complete, { once: true });
    link.addEventListener('error', complete, { once: true });
    if (shouldAppend) document.head.appendChild(link);
  });

  const loadScript = (src) => new Promise((resolve) => {
    if (window.OverlayScrollbarsGlobal || !src) {
      resolve();
      return;
    }

    const normalisedSrc = new URL(src, document.baseURI).href;
    let script = Array.from(document.scripts).find((item) => item.src === normalisedSrc);

    const complete = () => resolve();
    if (script) {
      script.addEventListener('load', complete, { once: true });
      script.addEventListener('error', complete, { once: true });
      return;
    }

    script = document.createElement('script');
    script.src = normalisedSrc;
    script.async = true;
    script.dataset.dgOptionalAsset = 'overlayscrollbars';
    script.addEventListener('load', complete, { once: true });
    script.addEventListener('error', complete, { once: true });
    document.head.appendChild(script);
  });

  overlayScrollbarsPromise = Promise.all([
    loadStylesheet(styleSrc),
    loadScript(scriptSrc)
  ]).then(() => window.OverlayScrollbarsGlobal || null);

  return overlayScrollbarsPromise;
}

function initOverlayScrollbar(slider) {
  if (!slider || slider.classList.contains('slider--no-scrollbar')
    || slider.dataset.dgOverlayScrollbar) return;

  slider.dataset.dgOverlayScrollbar = 'loading';
  loadOverlayScrollbars().then((library) => {
    if (!library?.OverlayScrollbars || !slider.isConnected) {
      delete slider.dataset.dgOverlayScrollbar;
      return;
    }

    library.OverlayScrollbars({
      target: slider.parentElement,
      elements: { viewport: slider }
    }, {});
    slider.dataset.dgOverlayScrollbar = 'ready';
  });
}

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 * @param {Element} [excludeVideo] - Video element to not pause.
 */
function pauseAllMedia(el = document, excludeVideo = null) {
  el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
    if (video === excludeVideo) return;
    const component = video.closest('video-component');
    if (component && component.dataset.background === 'true') return;

    if (video.matches('.js-youtube')) {
      video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
    } else if (video.matches('.js-vimeo')) {
      video.contentWindow.postMessage('{ "method": "pause" }', '*');
    } else {
      video.pause();
    }
  });

  el.querySelectorAll('product-model').forEach((model) => {
    try {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    } catch {
      // Do nothing
    }
  });
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const loadBtn = this.querySelector('.js-load-media');
    if (loadBtn) {
      loadBtn.addEventListener('click', this.loadContent.bind(this));
    } else {
      this.addObserver();
    }
  }

  /**
   * Adds an Intersection Observer to load the content when viewport scroll is near
   */
  addObserver() {
    if ('IntersectionObserver' in window === false) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent(false, false, 'observer');
          observer.unobserve(this);
        }
      });
    }, { rootMargin: '0px 0px 1000px 0px' });

    observer.observe(this);
  }

  /**
   * Loads the deferred media.
   * @param {boolean} [focus=true] - Focus the deferred media element after loading.
   * @param {boolean} [pause=true] - Whether to pause all media after loading.
   * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
   */
  loadContent(focus = true, pause = true, loadTrigger = 'click') {
    if (pause) pauseAllMedia();
    if (this.getAttribute('loaded') !== null) return;

    this.loadTrigger = loadTrigger;
    const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
    this.appendChild(content);
    this.setAttribute('loaded', '');

    const deferredEl = this.querySelector('video, model-viewer, iframe');
    if (deferredEl && focus) deferredEl.focus();
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.panel = this.toggle.nextElementSibling;
    this.transitionsEnabled = null;
    this.groupContainer = this.dataset.groupContainer
      ? this.closest(this.dataset.groupContainer) : null;

    this.toggle.addEventListener('click', this.handleToggle.bind(this));
    this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
  }

  /**
   * Handles 'click' events on the summary element.
   * @param {object} evt - Event object.
   */
  handleToggle(evt) {
    if (this.transitionsEnabled === null) this.checkTransitionsEnabled();
    if (!this.transitionsEnabled) return;

    evt.preventDefault();

    if (!this.disclosure.open) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Check if transitions are enabled on this disclosure, saving this in this.transitionsEnabled
   */
  checkTransitionsEnabled() {
    // Style check may cause break in Chrome dev tools - do not perform on init
    this.transitionsEnabled = window.getComputedStyle(this.panel).transitionDuration !== '0s';
  }

  /**
   * Handles 'transitionend' events on the details element.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;

    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }

    this.panel.removeAttribute('style');

    if (this.groupContainer) {
      this.groupContainer.style.minHeight = null;
    }
  }

  /**
   * Adds inline 'height' style to the content element, to trigger open transition.
   */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  /**
   * Opens the details element.
   */
  open() {
    // If we're in a group container, close any open disclosures in the group
    if (this.groupContainer) {
      this.groupContainer.style.minHeight = `${this.groupContainer.getBoundingClientRect().height}px`;
      const openDisclosure = this.groupContainer.querySelector('details-disclosure:has(.disclosure[open])');
      if (openDisclosure) openDisclosure.close();
    }

    // Set content 'height' to zero before opening the details element.
    this.panel.style.height = '0';

    // Open the details element
    this.disclosure.open = true;

    // Set content 'height' to its scroll height, to enable CSS transition.
    this.addContentHeight();
  }

  /**
   * Closes the details element.
   */
  close() {
    // Set content height to its scroll height, to enable transition to zero.
    this.addContentHeight();

    // Add class to enable styling of content or toggle icon before or during close transition.
    this.disclosure.classList.add('is-closing');

    // Set content height to zero to trigger the transition.
    // Slight delay required to allow scroll height to be applied before changing to '0'.
    setTimeout(() => {
      this.panel.style.height = '0';
    });
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

const trapFocusHandlers = {};

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
  const focusableEls = Array.from(
    container.querySelectorAll('summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])')
  );

  let firstEl = null;
  let lastEl = null;
  const isVisible = (el) => el.offsetParent && getComputedStyle(el).visibility !== 'hidden';

  const setFirstLastEls = () => {
    for (let i = 0; i < focusableEls.length; i += 1) {
      if (isVisible(focusableEls[i])) {
        firstEl = focusableEls[i];
        break;
      }
    }
    for (let i = focusableEls.length - 1; i >= 0; i -= 1) {
      if (isVisible(focusableEls[i])) {
        lastEl = focusableEls[i];
        break;
      }
    }
  };

  removeTrapFocus();

  trapFocusHandlers.focusin = (evt) => {
    setFirstLastEls();

    if (evt.target !== container && evt.target !== lastEl && evt.target !== firstEl) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (evt) => {
    if (evt.code !== 'Tab') return;

    setFirstLastEls();

    // If tab pressed on last focusable element, focus the first element.
    if (evt.target === lastEl && !evt.shiftKey) {
      evt.preventDefault();
      firstEl.focus();
    }

    //  If shift + tab pressed on the first focusable element, focus the last element.
    if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
      evt.preventDefault();
      lastEl.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  (elementToFocus || container).focus();
}

class Modal extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
    this.close();
  }

  /**
   * Opens the modal.
   * @param {Element} opener - Modal opener element.
   */
  open(opener) {
    this.setAttribute('open', '');
    this.openedBy = opener;

    trapFocus(this);
    window.pauseAllMedia();

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

    // Add event listener (for while modal is open).
    this.addEventListener('keyup', this.keyupHandler);

    // Wrap tables in a '.scrollable-table' element for a better mobile experience.
    this.querySelectorAll(':not(.scrollable-table) > table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'scrollable-table';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  /**
   * Closes the modal.
   */
  close() {
    this.removeAttribute('open');

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Remove event listener added on modal opening.
    this.removeEventListener('keyup', this.keyupHandler);
  }
}

customElements.define('modal-dialog', Modal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.getElementById(this.dataset.modal);
      if (modal) modal.open(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this._intersectionObserver = initLazyScript(this, this.init.bind(this));
  }

  disconnectedCallback() {
    this._intersectionObserver?.disconnect();
  }

  init() {
    this.mainImage = this.querySelector('.card__main-image');
    this.links = this.querySelectorAll('.js-prod-link');
    this.quickAddBtn = this.querySelector('.js-quick-add');
    this.carouselSlider = this.querySelector('product-card-image-slider');

    if (this.quickAddBtn) {
      this.productUrl = this.quickAddBtn.dataset.productUrl;
    } else if (this.links.length) {
      this.productUrl = this.links[0].href;
    }

    this.addEventListener('change', this.handleSwatchChange.bind(this));
  }

  /**
   * Handles 'change' events in the product card swatches.
   * @param {object} evt - Event object.
   */
  handleSwatchChange(evt) {
    if (!evt.target.matches('.opt-btn')) return;

    // Swap current card image to selected variant image.
    if (evt.target.dataset.mediaId && evt.target.dataset.mediaSrc && !this.carouselSlider && this.mainImage) {
      if (evt.target.dataset.mediaSrcset) {
        this.mainImage.srcset = evt.target.dataset.mediaSrcset;
      }
      this.mainImage.src = evt.target.dataset.mediaSrc;
      this.mainImage.dataset.mediaId = evt.target.dataset.mediaId;

      if (evt.target.dataset.mediaAlt !== undefined) {
        this.mainImage.alt = evt.target.dataset.mediaAlt;
      }
      if (evt.target.dataset.mediaWidth) {
        this.mainImage.width = evt.target.dataset.mediaWidth;
      }
      if (evt.target.dataset.mediaHeight) {
        this.mainImage.height = evt.target.dataset.mediaHeight;
      }
    }

    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set('variant', evt.target.dataset.variantId);

    // Update link hrefs to url of selected variant.
    this.links.forEach((link) => {
      link.href = url.toString();
    });

    // Update the Quick Add button data.
    if (this.quickAddBtn) {
      this.quickAddBtn.dataset.selectedColor = evt.target.value;
    }
  }
}

customElements.define('product-card', ProductCard);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.qty-input__input');
    this.currentQty = this.input.value;
    this.changeEvent = new Event('change', { bubbles: true });

    this.addEventListener('click', this.handleClick.bind(this));
    this.input.addEventListener('focus', QuantityInput.handleFocus);
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  /**
   * Handles 'click' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (!evt.target.matches('.qty-input__btn')) return;
    evt.preventDefault();

    this.currentQty = this.input.value;

    if (evt.target.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (this.input.value !== this.currentQty) {
      this.input.dispatchEvent(this.changeEvent);
      this.currentQty = this.input.value;
    }
  }

  /**
   * Handles 'focus' events on the quantity input element.
   * @param {object} evt - Event object.
   */
  static handleFocus(evt) {
    if (window.matchMedia('(pointer: fine)').matches) {
      evt.target.select();
    }
  }

  /**
   * Handles 'keydown' events on the input field.
   * @param {object} evt - Event object.
   */
  handleKeydown(evt) {
    if (evt.key !== 'Enter') return;
    evt.preventDefault();

    if (this.input.value !== this.currentQty) {
      this.input.blur();
      this.input.focus();
      this.currentQty = this.input.value;
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.js-overlay');
  }

  /**
   * Handles a 'click' event on the drawer.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
      this.close();
    }
  }

  /**
   * Opens the drawer.
   * @param {Element} [opener] - Element that triggered opening of the drawer.
   * @param {Element} [elementToFocus] - Element to focus after drawer opened.
   * @param {Function} [callback] - Callback function to trigger after the open has completed
   */
  open(opener, elementToFocus, callback) {
    this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-open`, {
      bubbles: true
    }));

    this.overlay.classList.add('is-visible');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.opener = opener;

    trapFocus(this, elementToFocus);

    // Create event handler variables (so the bound event listeners can be removed).
    this.clickHandler = this.clickHandler || this.handleClick.bind(this);
    this.keyupHandler = (evt) => {
      if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
      this.close();
    };

    // Add event listeners (for while drawer is open).
    this.addEventListener('click', this.clickHandler);
    this.addEventListener('keyup', this.keyupHandler);
    this.overlay.addEventListener('click', this.clickHandler);

    // Handle events after the drawer opens
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-open`, {
        bubbles: true
      }));
    }, transitionDuration);
  }

  /**
   * Closes the drawer.
   * @param {Function} [callback] - Call back function to trigger after the close has completed
   */
  close(callback) {
    this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-close`, {
      bubbles: true
    }));

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-visible');

    removeTrapFocus(this.opener);

    // Remove event listeners added on drawer opening.
    this.removeEventListener('click', this.clickHandler);
    this.removeEventListener('keyup', this.keyupHandler);
    this.overlay.removeEventListener('click', this.clickHandler);

    // Handle events after the drawer closes
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-close`, {
        bubbles: true
      }));
    }, transitionDuration);
  }
}

customElements.define('side-drawer', SideDrawer);

window.addEventListener(
  'resize',
  debounce(() => {
    window.dispatchEvent(new CustomEvent('on:debounced-resize'));
  })
);

/**
 * Keeps a record of the height of the first occurrence of the given child element and stores it in
 * a css variable called `--[selector without the . or #]-height`. This height is kept up to date
 * when the browser changes size or the child element mutates.
 *
 * The selector must only match one element, and don't nest watched elements.
 *
 * Example usage:
 * <slide-show data-css-var-height=".quick-nav">
 * ... will result in:
 * <slide-show data-css-var-height=".quick-nav" style="--quick-nav-height: 483px;">
 */
const cssVarHeightWatchRegistry = new WeakMap();
const cssVarHeightWatchParents = new Set();
let cssVarHeightGlobalListenersInstalled = false;

function updateCssVarHeightWatch(parentElem) {
  if (!parentElem?.isConnected) return;

  parentElem.dataset.cssVarHeight.split(',').forEach((rawSelector) => {
    const selector = rawSelector.trim();
    const elemToWatch = parentElem.querySelector(selector);
    if (!elemToWatch) return;

    const variableName = `--${selector.replace(/^([#.])/, '')}-height`;
    parentElem.style.setProperty(
      variableName,
      `${elemToWatch.getBoundingClientRect().height.toFixed(2)}px`
    );
  });
}

function destroyCssVarHeightWatch(parentElem) {
  const watch = cssVarHeightWatchRegistry.get(parentElem);
  if (!watch) return;

  watch.mutationObserver?.disconnect();
  watch.resizeObserver?.disconnect();
  if (watch.frame) window.cancelAnimationFrame(watch.frame);
  cssVarHeightWatchRegistry.delete(parentElem);
  cssVarHeightWatchParents.delete(parentElem);
}

function registerCssVarHeightWatch(parentElem) {
  if (cssVarHeightWatchRegistry.has(parentElem)) {
    updateCssVarHeightWatch(parentElem);
    return;
  }

  const watch = {
    frame: 0,
    mutationObserver: null,
    resizeObserver: null
  };

  const scheduleUpdate = () => {
    if (watch.frame) return;
    watch.frame = window.requestAnimationFrame(() => {
      watch.frame = 0;
      if (parentElem.isConnected) {
        updateCssVarHeightWatch(parentElem);
      } else {
        destroyCssVarHeightWatch(parentElem);
      }
    });
  };

  const observeSizedElements = () => {
    if (!watch.resizeObserver) return;
    watch.resizeObserver.disconnect();
    parentElem.dataset.cssVarHeight.split(',').forEach((rawSelector) => {
      const elemToWatch = parentElem.querySelector(rawSelector.trim());
      if (elemToWatch) watch.resizeObserver.observe(elemToWatch);
    });
  };

  if ('ResizeObserver' in window) {
    watch.resizeObserver = new ResizeObserver(scheduleUpdate);
  }

  if ('MutationObserver' in window) {
    watch.mutationObserver = new MutationObserver((mutations) => {
      // La mise à jour de la variable modifie le style du parent lui-même ;
      // l'ignorer évite une boucle d'observation sans masquer les changements
      // de classe/style des éléments réellement mesurés.
      const relevantMutations = mutations.filter(
        (mutation) => mutation.target !== parentElem || mutation.attributeName !== 'style'
      );
      if (!relevantMutations.length) return;
      if (relevantMutations.some((mutation) => mutation.type === 'childList')) {
        observeSizedElements();
      }
      scheduleUpdate();
    });
    watch.mutationObserver.observe(parentElem, {
      childList: true,
      attributes: true,
      subtree: true
    });
  }

  cssVarHeightWatchRegistry.set(parentElem, watch);
  cssVarHeightWatchParents.add(parentElem);
  observeSizedElements();
  updateCssVarHeightWatch(parentElem);
}

function updateAllCssVarHeightWatches() {
  cssVarHeightWatchParents.forEach((parentElem) => {
    if (parentElem.isConnected) {
      updateCssVarHeightWatch(parentElem);
    } else {
      destroyCssVarHeightWatch(parentElem);
    }
  });
}

function initCssVarHeightWatch(scope = document) {
  const parentElems = [];
  if (scope instanceof Element && scope.matches('[data-css-var-height]')) {
    parentElems.push(scope);
  }
  scope.querySelectorAll?.('[data-css-var-height]').forEach((parentElem) => {
    parentElems.push(parentElem);
  });
  parentElems.forEach(registerCssVarHeightWatch);

  if (!cssVarHeightGlobalListenersInstalled) {
    cssVarHeightGlobalListenersInstalled = true;
    window.addEventListener('on:debounced-resize', updateAllCssVarHeightWatches);
    document.addEventListener('on:css-var-height:update', updateAllCssVarHeightWatches);
  }
}

document.addEventListener('DOMContentLoaded', () => initCssVarHeightWatch(document));
document.addEventListener('shopify:section:load', (evt) => initCssVarHeightWatch(evt.target));
document.addEventListener('shopify:section:unload', (evt) => {
  if (evt.target.matches?.('[data-css-var-height]')) destroyCssVarHeightWatch(evt.target);
  evt.target.querySelectorAll?.('[data-css-var-height]').forEach(destroyCssVarHeightWatch);
});

/**
 * Provides convenient utility functions for interacting with elements
 */
(() => {
  theme.elementUtil = {};

  /**
   * Allows for removal of elements in one line of code
   * @param {object} elem - Element to remove
   */
  theme.elementUtil.remove = (elem) => {
    if (elem) {
      if (typeof elem.remove === 'function') {
        elem.remove();
      } else {
        elem.forEach((thisElem) => {
          thisElem.remove();
        });
      }
    }
  };

  /**
   * Checks if the passed element is in viewport or not
   * @param {object} elem - Element to check the view of
   * @returns {boolean}
   */
  theme.elementUtil.isInViewport = (elem) => {
    const rect = elem.getBoundingClientRect();
    return (
      Math.round(rect.top) >= 0
      && Math.round(rect.left) >= 0
      && Math.round(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight)
      && Math.round(rect.right) <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
})();

/**
 * Utility functions for interacting with LocalStorage/SessionStorage
 */
(() => {
  theme.storageUtil = {};

  theme.storageUtil.set = (key, value, isSession) => {
    if (isSession) {
      sessionStorage.setItem(
        `cc-${key}`,
        typeof value === 'object' ? JSON.stringify(value) : value
      );
    } else {
      localStorage.setItem(`cc-${key}`, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  };

  theme.storageUtil.get = (key, isJson, isSession) => {
    let value = isSession ? sessionStorage.getItem(`cc-${key}`) : localStorage.getItem(`cc-${key}`);
    if (isJson) {
      value = JSON.parse(value);
    }
    return value;
  };

  theme.storageUtil.remove = (key, isSession) => {
    if (isSession) {
      sessionStorage.removeItem(`cc-${key}`);
    } else {
      localStorage.removeItem(`cc-${key}`);
    }
  };
})();

class StoreHeader extends HTMLElement {
  constructor() {
    super();
    this.menu = this.querySelector('.main-menu__content');
    this.searchToggle = this.querySelector('.js-show-search');
    this.searchToggleLeft = this.querySelector('.js-show-search-left');
    this.mobNavToggle = this.querySelector('.main-menu__toggle');
    this.shakeyCartIcon = this.querySelector('.header__icon--cart-shake');
    this.headerGroupSections = document.querySelectorAll('.shopify-section-group-header-group');

    this.stickyInitialised = false;
    this.stickyTransitioning = false;
    this.lastScrollPos = 0;
    this.scrollHandler = this.handleScroll.bind(this);
    this.scrollIdleTimer = null;
    this.scrollIdleDelay = 1200;

    this.headerTransitionSpeed = parseFloat(
      getComputedStyle(this).getPropertyValue('--header-transition-speed')
    );
    this.headerTransitionFrame = null;

    window.setHeaderHeight();
    this.bindEvents();
    this.init();

    document.addEventListener('DOMContentLoaded', this.setMenuHeight.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('on:debounced-resize', this.resizeHandler);
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.breakpointChangeHandler) {
      window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }
    if (this.headerTransitionFrame) {
      window.cancelAnimationFrame(this.headerTransitionFrame);
    }
    this.clearScrollIdleReveal();
  }

  bindEvents() {
    this.resizeHandler = this.resizeHandler || this.updateHeaderHeights.bind(this);
    window.addEventListener('on:debounced-resize', this.resizeHandler);

    this.mobNavToggle.addEventListener('click', window.setHeaderHeight);
    this.mobNavToggle.addEventListener('click', this.setHeaderEnd.bind(this));

    if (this.dataset.isSticky) {
      this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);
      window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    }

    if (this.dataset.isSearchMinimised) {
      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', this.handleSearchToggleClick.bind(this));
      }

      if (this.searchToggleLeft) {
        this.searchToggleLeft.addEventListener('click', this.handleSearchToggleClick.bind(this));
      }
    }
  }

  /**
   * Init sticky behaviour of the header
   */
  init() {
    this.updateHeaderHeights();

    if (this.dataset.isSticky) {
      if (theme.mediaMatches.md && !this.stickyInitialised) {
        this.stickyInitialised = true;
        // Animate the menu in/out on scroll up/down
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }

      setTimeout(() => {
        document.querySelector('.cc-header').classList.add('cc-header--sticky');
      });

      this.setMenuHeight();
      this.setHeaderEnd();
    }

    // Stop the cart icon from shaking when necessary
    if (this.shakeyCartIcon) {
      let pageCount = theme.storageUtil.get('shake-page-count', false, true);
      pageCount = pageCount ? parseInt(pageCount, 10) + 1 : 1;
      const shakeFrequency = parseInt(this.shakeyCartIcon.dataset.shakeFrequency, 10);
      if (pageCount < shakeFrequency) {
        this.shakeyCartIcon.classList.remove('header__icon--cart-shake');
      } else {
        pageCount = 0;
      }
      theme.storageUtil.set('shake-page-count', pageCount, true);
    }
  }

  /**
   * Toggles visibility of the search bar
   * @param {object} evt - Event object
   */
  handleSearchToggleClick(evt) {
    evt.preventDefault();
    const searchBar = this.querySelector('.js-search-bar');
    if (this.classList.contains('search-is-collapsed')) {
      this.classList.remove('search-is-collapsed');

      // Wait for reveal animation to complete
      setTimeout(() => {
        this.classList.add('search-is-visible');
        const searchInput = searchBar.querySelector('.js-search-input');
        searchInput.focus();
        window.setHeaderHeight();
      }, this.headerTransitionSpeed);
    } else {
      this.classList.remove('search-is-visible');

      setTimeout(() => {
        this.classList.add('search-is-collapsed');
      });

      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    }
  }

  /**
   * Wrapper for calling the two below
   */
  updateHeaderHeights() {
    if (theme.mediaMatches.md) {
      this.setMenuHeight();
      setTimeout(window.setHeaderHeight, this.headerTransitionSpeed);
    } else {
      window.setHeaderHeight();
    }

    // Set a css variable to record where the page content starts
    if (this.headerGroupSections && this.headerGroupSections.length > 0) {
      let headerGroupHeight = 0;
      this.headerGroupSections.forEach((section) => {
        headerGroupHeight += section.getBoundingClientRect().height;
      });

      if (headerGroupHeight > 0) {
        document.documentElement.style.setProperty(
          '--content-start',
          `${headerGroupHeight.toFixed(1)}px`
        );
      }
    }
  }

  /**
   * Set a css variable to the height of the menu links
   */
  setMenuHeight() {
    if (this.menu && this.menu.clientHeight) {
      this.style.setProperty('--menu-height', `${this.menu.clientHeight + 16}px`);
      document.documentElement.style.setProperty('--header-height', `${this.clientHeight}px`);
    }
  }

  /**
   * Handles 'scroll' event on the header
   */
  handleScroll() {
    this.clearScrollIdleReveal();

    if (!document.body.classList.contains('fixed') && !this.stickyTransitioning) {
      const scrollTop = document.documentElement.scrollTop;

      if (scrollTop < 200) {
        this.show();
      } else if (this.lastScrollPos < scrollTop) {
        this.hide();
        this.queueScrollIdleReveal();
      } else if (this.lastScrollPos - 5 > scrollTop) {
        this.show();
      } else {
        this.queueScrollIdleReveal();
      }

      this.lastScrollPos = scrollTop;
    } else if (!document.body.classList.contains('fixed')) {
      this.queueScrollIdleReveal();
    }
  }

  /**
   * Reveal desktop menu after the user stops scrolling.
   */
  queueScrollIdleReveal() {
    if (
      !this.dataset.isSticky
      || !theme.mediaMatches.md
      || !this.classList.contains('is-out')
      || document.documentElement.scrollTop < 200
    ) {
      return;
    }

    this.scrollIdleTimer = window.setTimeout(() => {
      this.scrollIdleTimer = null;

      if (
        this.dataset.isSticky
        && theme.mediaMatches.md
        && this.classList.contains('is-out')
        && !document.body.classList.contains('fixed')
        && !this.stickyTransitioning
        && document.documentElement.scrollTop >= 200
      ) {
        this.show();
      }
    }, this.scrollIdleDelay);
  }

  /**
   * Cancel pending desktop menu idle reveal.
   */
  clearScrollIdleReveal() {
    if (this.scrollIdleTimer) {
      window.clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = null;
    }
  }

  /**
   * Set a css variable to indicate where the nav ends on the page
   */
  setHeaderEnd() {
    const rect = this.getBoundingClientRect();
    const headerEnd = Number(rect.top + rect.height);
    document.documentElement.style.setProperty('--header-height', `${rect.height.toFixed(1)}px`);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty(
      '--header-end-padded',
      `${(headerEnd + (theme.mediaMatches.md ? 56 : 20)).toFixed(1)}px`
    );
  }

  /**
   * Keeps sticky elements aligned while the desktop menu collapses/reveals.
   */
  trackHeaderTransition() {
    const transitionDuration = Number.isFinite(this.headerTransitionSpeed)
      ? this.headerTransitionSpeed
      : 300;
    const start = performance.now();
    const duration = transitionDuration + 80;

    if (this.headerTransitionFrame) {
      window.cancelAnimationFrame(this.headerTransitionFrame);
    }

    const tick = (now) => {
      this.setHeaderEnd();

      if (now - start < duration) {
        this.headerTransitionFrame = window.requestAnimationFrame(tick);
      } else {
        this.headerTransitionFrame = null;
        this.setHeaderEnd();
      }
    };

    this.headerTransitionFrame = window.requestAnimationFrame(tick);
  }

  /**
   * Updates hidden class for header element
   */
  show() {
    const transitionDuration = Number.isFinite(this.headerTransitionSpeed)
      ? this.headerTransitionSpeed
      : 300;
    this.classList.remove('is-out');
    this.trackHeaderTransition();
    this.stickyTransitioning = true;
    setTimeout(() => {
      this.lastScrollPos = document.documentElement.scrollTop;
      this.stickyTransitioning = false;
      this.handleScroll();
      this.setHeaderEnd();
    }, transitionDuration);
  }

  /**
   * Updates hidden class for header element
   */
  hide() {
    if (!this.stickyTransitioning) {
      const transitionDuration = Number.isFinite(this.headerTransitionSpeed)
        ? this.headerTransitionSpeed
        : 300;
      this.classList.add('is-out');
      this.trackHeaderTransition();
      this.stickyTransitioning = true;
      setTimeout(() => {
        this.lastScrollPos = document.documentElement.scrollTop;
        this.stickyTransitioning = false;
        this.handleScroll();
        this.setHeaderEnd();
      }, transitionDuration);
    }
  }
}

customElements.define('store-header', StoreHeader);

class MainMenu extends HTMLElement {
  constructor() {
    super();
    this.mainDisclosure = this.querySelector('.main-menu__disclosure');
    this.mainToggle = this.querySelector('.main-menu__toggle');
    this.firstLevelMenuLinks = this.querySelectorAll('.js-nav-hover');
    this.firstLevelSingleLinks = this.querySelectorAll('.main-nav__item--primary:not(.main-nav__item-content)');
    this.nav = this.querySelector('.main-nav');
    this.navScroll = this.querySelector('[data-main-menu-scroll]');
    this.navScrollWrap = this.querySelector('[data-main-menu-scroll-wrap]');
    this.overlay = document.querySelector('.js-overlay');
    this.searchIcon = document.querySelector('.header__icons .js-show-search');
    this.megaSidebarTriggers = this.querySelectorAll('[data-mega-sidebar] [data-msd-trigger]');
    this.megaSidebarDirectGroups = this.querySelectorAll('[data-mega-sidebar] .msd__group--direct');
    this.elementsWhichCloseMenus = document.querySelectorAll('.js-closes-menu');

    this.isTouchEvent = false;
    this.childNavOpen = false;
    this.overlayOpen = false;

    this.addListeners();
    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('focusin', this.focusOutHandler);
    window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    window.removeEventListener('resize', this.navScrollResizeHandler);
    this.nav.removeEventListener('focusin', this.megaSidebarFocusHandler);
    if (this.mouseEnterMegaSidebarDirectHandler) {
      this.megaSidebarDirectGroups.forEach((group) => {
        group.removeEventListener('mouseenter', this.mouseEnterMegaSidebarDirectHandler);
      });
    }
    if (this.navScroll) this.navScroll.removeEventListener('scroll', this.navScrollHandler);
    if (this.navScroll) this.navScroll.removeEventListener('wheel', this.navWheelHandler);
    if (this.navScrollResizeObserver) this.navScrollResizeObserver.disconnect();

    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }
  }

  addListeners() {
    this.focusOutHandler = this.focusOutHandler || this.handleFocusOut.bind(this);
    this.breakpointChangeHandler = this.breakpointChangeHandler || this.init.bind(this);
    this.megaSidebarFocusHandler = this.megaSidebarFocusHandler || MainMenu.handleMegaSidebarFocus;
    this.navScrollHandler = this.navScrollHandler || this.handleNavScroll.bind(this);
    this.navWheelHandler = this.navWheelHandler || this.handleNavWheel.bind(this);
    this.navScrollResizeHandler = this.navScrollResizeHandler || this.updateNavScrollState.bind(this);

    this.mainDisclosure.addEventListener('transitionend', MainMenu.handleTransition.bind(this));
    this.mainToggle.addEventListener('click', this.handleMainMenuToggle.bind(this));
    this.nav.addEventListener('touchstart', () => { this.isTouchEvent = true; });
    this.nav.addEventListener('click', MainMenu.handleNavClick.bind(this));
    this.nav.addEventListener('focusin', this.megaSidebarFocusHandler);
    this.nav.addEventListener('touchend', () => setTimeout(() => { this.isTouchEvent = false; }, 100));
    if (this.navScroll) this.navScroll.addEventListener('scroll', this.navScrollHandler, { passive: true });
    if (this.navScroll) this.navScroll.addEventListener('wheel', this.navWheelHandler, { passive: false });
    window.addEventListener('focusin', this.focusOutHandler);
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
    window.addEventListener('resize', this.navScrollResizeHandler);

    if (Shopify.designMode) {
      this.blockSelectHandler = this.blockSelectHandler || this.handleBlockSelect.bind(this);
      this.blockDeselectHandler = this.blockDeselectHandler || this.handleBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
      document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
    }

    if (!theme.mediaMatches.md && this.searchIcon) {
      this.searchIcon.addEventListener('click', this.closeMainDisclosure.bind(this));
    }

    if (window.ResizeObserver && this.navScroll) {
      this.navScrollResizeObserver = new ResizeObserver(this.navScrollResizeHandler);
      this.navScrollResizeObserver.observe(this.navScroll);
      this.navScrollResizeObserver.observe(this.nav);
    }
  }

  /**
   * Throttles horizontal scroll state updates for desktop menu fades.
   */
  handleNavScroll() {
    if (this.navScrollTicking) return;
    this.navScrollTicking = true;
    window.requestAnimationFrame(() => {
      this.updateNavScrollState();
      this.navScrollTicking = false;
    });
  }

  /**
   * Lets a desktop mouse wheel move the horizontal menu when it overflows.
   * @param {object} evt - Wheel event.
   */
  handleNavWheel(evt) {
    if (!theme.mediaMatches.md || !this.navScroll || !this.navScrollWrap) return;
    this.updateNavMenuOpenState();
    if (this.navScrollWrap.classList.contains('is-menu-open')) return;

    const maxScroll = Math.max(this.navScroll.scrollWidth - this.navScroll.clientWidth, 0);
    if (maxScroll <= 2) return;

    const delta = Math.abs(evt.deltaX) > Math.abs(evt.deltaY) ? evt.deltaX : evt.deltaY;
    if (!delta) return;

    const currentScroll = this.navScroll.scrollLeft;
    const nextScroll = Math.min(Math.max(currentScroll + delta, 0), maxScroll);

    if (nextScroll !== currentScroll) {
      evt.preventDefault();
      this.navScroll.scrollLeft = nextScroll;
      this.updateNavScrollState();
    }
  }

  /**
   * Tracks whether a submenu is actively open so desktop scroll fades pause.
   */
  updateNavMenuOpenState() {
    if (!this.nav || !this.navScrollWrap) return;

    const isOpen = Boolean(this.nav.querySelector('details.is-open'));
    this.navScrollWrap.classList.toggle('is-menu-open', isOpen);
  }

  /**
   * Keeps desktop dropdowns visible when the main nav itself scrolls horizontally.
   * @param {Element} disclosure - Top-level disclosure element.
   */
  positionDesktopNavChild(disclosure) {
    if (!theme.mediaMatches.md || !this.navScroll || !this.navScrollWrap || !disclosure) return;

    const dropdownContainer = Array.from(disclosure.children)
      .find((child) => child.classList?.contains('main-nav__child'));
    const toggle = disclosure.querySelector('summary');

    if (!dropdownContainer || !this.navScroll.contains(dropdownContainer) || !toggle) return;

    const toggleRect = toggle.getBoundingClientRect();
    const navRect = this.navScrollWrap.getBoundingClientRect();
    const dropdownTop = Math.round(Math.max(toggleRect.bottom, navRect.bottom));

    dropdownContainer.style.setProperty('--main-menu-child-top', `${dropdownTop}px`);

    if (dropdownContainer.classList.contains('mega-nav')) {
      dropdownContainer.style.setProperty('--main-menu-child-left', '0px');
      dropdownContainer.style.setProperty('--main-menu-child-width', `${window.innerWidth}px`);
      return;
    }

    const gutter = 8;
    const preferredLeft = Math.max(gutter, Math.round(toggleRect.left));
    dropdownContainer.style.setProperty('--main-menu-child-left', `${preferredLeft}px`);
    dropdownContainer.style.removeProperty('--main-menu-child-width');

    const dropdownRect = dropdownContainer.getBoundingClientRect();
    if (dropdownRect.right > window.innerWidth - gutter) {
      const adjustedLeft = Math.max(gutter, Math.round(window.innerWidth - dropdownRect.width - gutter));
      dropdownContainer.style.setProperty('--main-menu-child-left', `${adjustedLeft}px`);
    }
  }

  /**
   * Clears desktop dropdown positioning after a menu closes.
   * @param {Element} disclosure - Top-level disclosure element.
   */
  resetDesktopNavChild(disclosure) {
    if (!disclosure) return;

    const dropdownContainer = Array.from(disclosure.children)
      .find((child) => child.classList?.contains('main-nav__child'));

    if (!dropdownContainer) return;

    dropdownContainer.style.removeProperty('--main-menu-child-top');
    dropdownContainer.style.removeProperty('--main-menu-child-left');
    dropdownContainer.style.removeProperty('--main-menu-child-width');
  }

  /**
   * Toggles left/right fade indicators when the desktop menu can scroll.
   */
  updateNavScrollState() {
    if (!this.navScroll || !this.navScrollWrap) return;

    if (!theme.mediaMatches.md) {
      this.navScrollWrap.classList.remove('can-scroll-left', 'can-scroll-right', 'is-scrollable', 'is-menu-open');
      return;
    }

    this.updateNavMenuOpenState();

    const activeDisclosure = this.nav.querySelector('details.is-open');
    if (activeDisclosure) this.positionDesktopNavChild(activeDisclosure);

    const maxScroll = Math.max(this.navScroll.scrollWidth - this.navScroll.clientWidth, 0);
    const currentScroll = Math.max(this.navScroll.scrollLeft, 0);
    const tolerance = 2;

    this.navScrollWrap.classList.toggle('is-scrollable', maxScroll > tolerance);
    this.navScrollWrap.classList.toggle('can-scroll-left', currentScroll > tolerance);
    this.navScrollWrap.classList.toggle('can-scroll-right', currentScroll < maxScroll - tolerance);
  }

  /**
   * Sets 'open' state of the main disclosure element.
   * @param {?object} evt - Event object.
   */
  init(evt) {
    if (!evt) {
      this.mainDisclosure.open = theme.mediaMatches.md;
    } else if (!theme.mediaMatches.md && !this.childNavOpen) {
      this.close(this.mainDisclosure);

      if (this.overlayOpen) this.toggleOverlay(false);
    } else {
      // If there's another menu open, close it.
      const activeDisclosure = this.nav.querySelector('details.is-open');
      if (activeDisclosure) {
        this.close(activeDisclosure);
      } else {
        MainMenu.open(this.mainDisclosure, false);
      }

      if (!this.childNavOpen) {
        if (this.overlayOpen) this.toggleOverlay(false);
      }
    }

    // Close the submenus (they're open for no-js)
    this.querySelectorAll('.child-nav--dropdown details[open]').forEach((childToggle) => {
      childToggle.removeAttribute('open');
    });

    // Les groupes du méga-menu unifié ne vivent pas dans .child-nav--dropdown :
    // le nettoyage ci-dessus ne les atteint pas. Sur mobile, un <details> resté
    // ouvert par un passage sur bureau déplierait le premier sous-rayon et
    // inverserait le premier appui dessus.
    if (!theme.mediaMatches.md) {
      this.querySelectorAll('[data-mega-sidebar] details[data-msd-group][open]').forEach((group) => {
        group.removeAttribute('open');
      });
    }

    this.updateNavScrollState();

    if (theme.device.hasHover) {
      // Add event handler (so the bound event listener can be removed)
      this.mouseEnterMenuLinkHandler = this.mouseEnterMenuLinkHandler
        || this.handleMouseEnterMenuLink.bind(this);
      this.mouseLeaveMenuLinkHandler = this.mouseLeaveMenuLinkHandler
        || this.handleMouseLeaveMenuLink.bind(this);
      this.mouseEnterSingleLinkHandler = this.mouseEnterSingleLinkHandler
        || this.handleMouseEnterSingleLink.bind(this);
      this.mouseLeaveSingleLinkHandler = this.mouseLeaveSingleLinkHandler
        || this.handleMouseLeaveSingleLink.bind(this);
      this.mouseEnterMenuCloserHandler = this.mouseEnterMenuCloserHandler
        || this.handleClose.bind(this);
      this.mouseEnterMegaSidebarHandler = this.mouseEnterMegaSidebarHandler
        || MainMenu.handleMegaSidebarMouseEnter;
      this.mouseEnterMegaSidebarDirectHandler = this.mouseEnterMegaSidebarDirectHandler
        || MainMenu.handleMegaSidebarDirectMouseEnter;

      // Bind listening events for mouse enter/leave a main menu link
      if (!this.mouseOverListening && theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.addEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.addEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.addEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.addEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.addEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = true;
      } else if (this.mouseOverListening && !theme.mediaMatches.md) {
        this.firstLevelMenuLinks.forEach((menuLink) => {
          menuLink.removeEventListener('mouseenter', this.mouseEnterMenuLinkHandler);
          menuLink.removeEventListener('mouseleave', this.mouseLeaveMenuLinkHandler);
        });
        this.firstLevelSingleLinks.forEach((singleLink) => {
          singleLink.removeEventListener('mouseenter', this.mouseEnterSingleLinkHandler);
          singleLink.removeEventListener('mouseleave', this.mouseLeaveSingleLinkHandler);
        });
        this.elementsWhichCloseMenus.forEach((elem) => {
          elem.removeEventListener('mouseenter', this.mouseEnterMenuCloserHandler);
        });
        this.mouseOverListening = false;
      }

      if (this.megaSidebarTriggers.length) {
        if (!this.mouseOverMegaSidebarListening && theme.mediaMatches.md) {
          this.megaSidebarTriggers.forEach((trigger) => {
            trigger.addEventListener('mouseenter', this.mouseEnterMegaSidebarHandler);
          });
          this.mouseOverMegaSidebarListening = true;
        } else if (this.mouseOverMegaSidebarListening && !theme.mediaMatches.md) {
          this.megaSidebarTriggers.forEach((trigger) => {
            trigger.removeEventListener('mouseenter', this.mouseEnterMegaSidebarHandler);
          });
          this.mouseOverMegaSidebarListening = false;
        }
      }

      if (this.megaSidebarDirectGroups.length) {
        if (!this.mouseOverMegaSidebarDirectListening && theme.mediaMatches.md) {
          this.megaSidebarDirectGroups.forEach((group) => {
            group.addEventListener('mouseenter', this.mouseEnterMegaSidebarDirectHandler);
          });
          this.mouseOverMegaSidebarDirectListening = true;
        } else if (this.mouseOverMegaSidebarDirectListening && !theme.mediaMatches.md) {
          this.megaSidebarDirectGroups.forEach((group) => {
            group.removeEventListener('mouseenter', this.mouseEnterMegaSidebarDirectHandler);
          });
          this.mouseOverMegaSidebarDirectListening = false;
        }
      }
    }
  }

  /**
   * Close the menu if the nav loses focus
   * @param {object} evt - Event object.
   */
  handleFocusOut(evt) {
    if (!this.contains(evt.target) && this.overlayOpen) this.handleClose();
  }

  /**
   * Activates a panel in the unified sidebar mega menu.
   * Direct links have no target and clear active panels through their own hover handler.
   * @param {Element} trigger - Sidebar summary trigger.
   */
  static activateMegaSidebarTrigger(trigger) {
    const root = trigger.closest('[data-mega-sidebar]');
    const group = trigger.closest('[data-msd-group]');
    if (!root || !group) return;

    const targetId = group.getAttribute('data-msd-target');
    if (!targetId) return;

    const panels = root.querySelectorAll('[data-msd-panel]');
    let targetPanel = null;

    panels.forEach((panel) => {
      if (panel.id === targetId) targetPanel = panel;
    });

    if (!targetPanel) return;

    root.querySelectorAll('[data-msd-group]').forEach((item) => {
      const isActive = item === group;
      item.classList.toggle('is-active', isActive);

      // Sur bureau, la liste des types EST le contenu de la porte : les
      // <details> restent tous ouverts, sans quoi le navigateur replie leur
      // contenu. C'est init() qui les referme au passage sous 769 px.
      if (theme.mediaMatches.md && item.matches('details')) {
        item.open = true;
      }
    });

    panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel === targetPanel);
    });
  }

  /**
   * Clears active panels in the unified sidebar mega menu.
   * @param {Element} root - Sidebar root.
   */
  static deactivateMegaSidebarPanels(root) {
    if (!root) return;

    root.querySelectorAll('.msd__group--panel.is-active').forEach((group) => {
      group.classList.remove('is-active');

      // Ne pas replier sur bureau : voir activateMegaSidebarTrigger.
      if (theme.mediaMatches.md && group.matches('details')) {
        group.open = true;
      }
    });

    root.querySelectorAll('[data-msd-panel].is-active').forEach((panel) => {
      panel.classList.remove('is-active');
    });
  }

  /**
   * Restores the default panel when a desktop sidebar mega menu opens.
   * @param {Element} root - Sidebar root.
   */
  static activateMegaSidebarDefault(root) {
    const defaultTrigger = root.querySelector('[data-msd-default]');
    if (defaultTrigger) MainMenu.activateMegaSidebarTrigger(defaultTrigger);
  }

  /**
   * Handles desktop hover on unified sidebar triggers.
   * @param {object} evt - Event object.
   */
  static handleMegaSidebarMouseEnter(evt) {
    MainMenu.activateMegaSidebarTrigger(evt.currentTarget);
  }

  /**
   * Handles desktop hover on unified sidebar direct links.
   * @param {object} evt - Event object.
   */
  static handleMegaSidebarDirectMouseEnter(evt) {
    if (!theme.mediaMatches.md) return;

    MainMenu.deactivateMegaSidebarPanels(evt.currentTarget.closest('[data-mega-sidebar]'));
  }

  /**
   * Handles keyboard focus on unified sidebar triggers.
   * @param {object} evt - Event object.
   */
  static handleMegaSidebarFocus(evt) {
    const trigger = evt.target.closest('[data-mega-sidebar] [data-msd-trigger]');
    if (trigger && theme.mediaMatches.md) {
      MainMenu.activateMegaSidebarTrigger(trigger);
    }
  }

  /**
   * Handles 'toggle' event on the main disclosure element.
   * @param {object} evt - Event object.
   */
  handleMainMenuToggle(evt) {
    evt.preventDefault();
    this.opener = this.mainToggle;

    if (!this.mainDisclosure.open) {
      MainMenu.open(this.mainDisclosure);
    } else {
      this.close(this.mainDisclosure, true);
    }
  }

  /**
   * Handles 'mouseenter' event on the main menu items using a timeout to infer hover intent
   * @param {object} evt - Event object
   */
  handleMouseEnterMenuLink(evt) {
    this.menuLinkTimeout = setTimeout(
      this.openMenuFromMouseEnter.bind(this, evt.target),
      Number.parseInt(this.dataset.menuSensitivity, 10)
    );
  }

  /**
   * Handles 'mouseleave' event on the main menu items - clears the timeout
   */
  handleMouseLeaveMenuLink() {
    if (this.menuLinkTimeout) clearTimeout(this.menuLinkTimeout);
  }

  /**
   * Handles 'mouseenter' event on links with no submenu items using a timeout to infer hover intent
   */
  handleMouseEnterSingleLink() {
    this.singleLinkTimeout = setTimeout(() => {
      this.handleClose();
    }, Number.parseInt(this.dataset.menuSensitivity, 10));
  }

  /**
   * Handles 'mouseleave' event on links with no submenu - clears the timeout
   */
  handleMouseLeaveSingleLink() {
    if (this.singleLinkTimeout) clearTimeout(this.singleLinkTimeout);
  }

  /**
   * Opens the menu being hovered over
   * @param {Element} menuElem - The menu element to open.
   */
  openMenuFromMouseEnter(menuElem) {
    trapFocus(menuElem);

    const disclosure = menuElem.closest('details');
    if (!disclosure.classList.contains('is-open')) {
      const activeDisclosure = this.nav.querySelector('details.is-open');

      // If there's another menu open, close it.
      if (activeDisclosure && activeDisclosure !== disclosure) {
        this.close(activeDisclosure);
      } else {
        this.toggleOverlay(!this.overlayOpen);
      }

      MainMenu.open(disclosure);
    }
  }

  /**
   * Handles 'click' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleNavClick(evt) {
    const mainMenuContent = evt.target.closest('.main-menu__content');
    let el = evt.target;

    const unifiedSidebarSummary = el.closest('[data-mega-sidebar] summary[data-msd-trigger]');
    if (unifiedSidebarSummary) {
      if (!theme.mediaMatches.md) {
        evt.preventDefault();

        const details = unifiedSidebarSummary.closest('details[data-msd-group]');
        const root = unifiedSidebarSummary.closest('[data-mega-sidebar]');
        if (details && root) {
          const willOpen = !details.open;

          root.querySelectorAll('details[data-msd-group][open]').forEach((openDetails) => {
            if (openDetails !== details) {
              openDetails.open = false;
              openDetails.classList.remove('is-active');
              const panel = openDetails.querySelector('[data-msd-panel]');
              if (panel) panel.classList.remove('is-active');
            }
          });

          details.open = willOpen;
          details.classList.toggle('is-active', willOpen);
          const panel = details.querySelector('[data-msd-panel]');
          if (panel) panel.classList.toggle('is-active', willOpen);
        }

        return;
      }

      MainMenu.activateMegaSidebarTrigger(unifiedSidebarSummary);

      // Le retour doit être inconditionnel. Sans lui, un appui sur un écran
      // tactile large retombait plus bas sur `isLargeTouchDevice &&
      // el.closest('summary')`, qui annule l'événement et referme le méga-menu :
      // la tuile ne naviguait pas et le défilement restait bloqué. Le test sur
      // '#' reprend le rôle de la ligne « Don't follow # links », désormais hors
      // d'atteinte pour cette branche.
      const sidebarLink = el.closest('a[href]');
      if (!sidebarLink || sidebarLink.getAttribute('href') === '#') evt.preventDefault();
      return;
    }

    // Don't follow # links
    if (evt.target.href && evt.target.href.endsWith('#')) evt.preventDefault();

    // If we're on a device without hover on a larger screen, open the menu
    if (theme.mediaMatches.md && !theme.device.hasHover) {
      el = evt.target.closest('.js-toggle');
      if (!el) return;
    }

    const isLargeTouchDevice = this.isTouchEvent && theme.device.hasHover && theme.mediaMatches.md;

    if (!el.matches('.js-toggle,.js-back') && !isLargeTouchDevice) return;

    if (el.closest('summary.child-nav__item--toggle') && isLargeTouchDevice) return;

    const disclosure = el.closest('details');

    if (theme.mediaMatches.md && theme.device.hasHover && !isLargeTouchDevice) {
      disclosure.classList.toggle('is-open');
      return;
    }

    this.opener = el;

    if (el.matches('.js-toggle') || (isLargeTouchDevice && el.closest('summary'))) {
      evt.preventDefault();

      if (!theme.mediaMatches.md) {
        mainMenuContent.classList.add('main-menu__content--no-focus');
      }

      if (!disclosure.classList.contains('is-open')) {
        this.childNavOpen = true;

        const activeDisclosure = this.nav.querySelector('details.is-open');
        // If there's another menu open, close it.
        if (activeDisclosure && activeDisclosure !== disclosure) {
          this.close(activeDisclosure);
        } else if (theme.mediaMatches.md) {
          this.toggleOverlay(!this.overlayOpen);
        }

        MainMenu.open(disclosure);
      } else {
        this.close(disclosure, true);
        this.childNavOpen = false;
        this.toggleOverlay(false);
      }
    } else if (el.matches('.js-back')) {
      evt.preventDefault();
      this.close(disclosure, true);
      this.childNavOpen = false;

      if (!theme.mediaMatches.md) {
        mainMenuContent.classList.remove('main-menu__content--no-focus');
      }
    }
  }

  /**
   * Handles 'transitionend' event on the nav.
   * @param {object} evt - Event object.
   */
  static handleTransition(evt) {
    const disclosure = evt.target.closest('details');

    if (disclosure.classList.contains('is-closing')) {
      disclosure.classList.remove('is-closing');
      disclosure.open = false;

      removeTrapFocus();
      this.opener = null;
    }
  }

  /**
   * Handles a 'click' event on the overlay and a 'keyup' event on the nav.
   * @param {object} evt - Event object.
   */
  handleClose(evt) {
    if (evt && evt.type === 'keyup' && evt.key !== 'Escape') return;

    const disclosure = theme.mediaMatches.md
      ? this.nav.querySelector('details.is-open')
      : this.mainDisclosure;

    if (disclosure) {
      this.close(disclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Toggles visibility of the background overlay.
   * @param {boolean} show - Show the overlay.
   */
  toggleOverlay(show) {
    this.overlayOpen = show;
    this.overlay.classList.toggle('overlay--nav', show);
    this.overlay.classList.toggle('is-visible', show);

    if (show) {
      // Add event handler (so the bound event listener can be removed).
      this.closeHandler = this.closeHandler || this.handleClose.bind(this);

      // Add event listeners (for while the nav is open).
      this.overlay.addEventListener('click', this.closeHandler);
      this.nav.addEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.addEventListener('mouseenter', this.closeHandler);
      }
    } else {
      // Remove event listener added on nav opening.
      this.overlay.removeEventListener('click', this.closeHandler);
      this.nav.removeEventListener('keyup', this.closeHandler);

      if (theme.mediaMatches.md) {
        this.overlay.removeEventListener('mouseenter', this.closeHandler);
      }
    }
  }

  /**
   * Closes the main nav menu
   */
  closeMainDisclosure() {
    if (this.mainDisclosure.classList.contains('is-open')) {
      this.close(this.mainDisclosure, true);
      this.toggleOverlay(false);
      this.childNavOpen = false;
    }
  }

  /**
   * Updates open/opening classes for disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [mainMenuOpen=true] - Main menu is open.
   */
  static open(el, mainMenuOpen = true) {
    const mainMenu = el.closest('main-menu');

    el.open = true;
    mainMenu?.querySelector('[data-main-menu-scroll-wrap]')?.classList.add('is-menu-open');
    mainMenu?.positionDesktopNavChild?.(el);

    // Cap the max width of grandchildren on desktop their contents don't widen the dropdown
    if (theme.mediaMatches.md && !el.classList.contains('js-mega-nav')) {
      // If the nav menu spills off the right of the screen, shift it to the left
      const dropdownContainer = el.querySelector('.main-nav__child');
      if (dropdownContainer?.getBoundingClientRect().right > window.innerWidth && !mainMenu?.navScroll?.contains(dropdownContainer)) {
        dropdownContainer.classList.add('main-nav__child--offset-right');
      }

      const dropdown = el.querySelector('.child-nav--dropdown');
      if (dropdown) {
        dropdown.querySelectorAll('.main-nav__grandchild').forEach((grandchildElem) => {
          grandchildElem.style.maxWidth = `${dropdown.clientWidth}px`;
        });
      }
    } else if (theme.mediaMatches.md && el.querySelector('[data-mega-sidebar]')) {
      el.querySelectorAll('[data-mega-sidebar]').forEach((root) => {
        MainMenu.activateMegaSidebarDefault(root);
      });
    }

    // Slight delay required before starting transitions.
    setTimeout(() => {
      el.classList.remove('is-closing');
      el.classList.add('is-open');
      mainMenu?.positionDesktopNavChild?.(el);
      mainMenu?.updateNavMenuOpenState?.();
    });

    if (mainMenuOpen) {
      removeTrapFocus();
      trapFocus(el);

      if (theme.mediaMatches.md || el.classList.contains('main-menu__disclosure')) {
        document.body.classList.add('overflow-hidden');

        if (!theme.mediaMatches.md && !document.querySelector('store-header[data-is-sticky="true"]')) {
          document.body.classList.add('fixed');
        }
      }
    }
  }

  /**
   * Updates close/closing classes of a disclosure element.
   * @param {Element} el - Disclosure element.
   * @param {boolean} [transition=false] - Close action has a CSS transition.
   */
  close(el, transition = true) {
    el.classList.remove('is-open');

    if (transition) {
      el.classList.add('is-closing');
    } else {
      el.classList.remove('is-closing');
      el.open = false;

      removeTrapFocus(this.opener);
      this.opener = null;
    }

    this.updateNavMenuOpenState();

    setTimeout(() => {
      const offsetMenu = el.querySelector('.main-nav__child--offset-right');
      if (offsetMenu) offsetMenu.classList.remove('main-nav__child--offset-right');
      this.resetDesktopNavChild(el);
      this.updateNavScrollState();
    }, 200);

    if (theme.mediaMatches.md || el.classList.contains('main-menu__disclosure')) {
      document.body.classList.remove('overflow-hidden', 'fixed');
    }
  }

  /**
   * Decide whether to show a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockSelect(evt) {
    const activeDisclosure = this.nav.querySelector('details.is-open');
    if (activeDisclosure) {
      this.close(activeDisclosure, false);
    }

    if (evt.target.matches('.js-mega-nav')) {
      MainMenu.open(evt.target, false);
      this.toggleOverlay(true);
    }
  }

  /**
   * Decide whether to hide a particular mega menu
   * @param {object} evt - Event object
   */
  handleBlockDeselect(evt) {
    if (evt.target.matches('.js-mega-nav')) {
      this.close(evt.target, false);
      this.toggleOverlay(false);
    }
  }
}

customElements.define('main-menu', MainMenu);

class CarouselSlider extends HTMLElement {
  constructor() {
    super();
    this._intersectionObserver = initLazyScript(this, this.init.bind(this));
  }

  disconnectedCallback() {
    this._intersectionObserver?.disconnect();
    window.removeEventListener('on:debounced-resize', this.breakpointChangeHandler);
  }

  init() {
    // Ignore nested slides
    this.slides = Array.from(this.querySelector('.slider__item').parentElement.children).filter((child) => !child.hasAttribute('hidden'));
    if (this.slides.length < 2) return;
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    this.nav = this.querySelector(`.slider-nav__btn[aria-controls='${this.slider.id}']`)?.closest('.slider-nav');
    this.rtl = document.dir === 'rtl';
    this.breakpointChangeHandler = this.breakpointChangeHandler
      || this.handleBreakpointChange.bind(this);

    if (this.nav) {
      this.prevBtn = this.nav.querySelector('button[name="prev"]');
      this.nextBtn = this.nav.querySelector('button[name="next"]');
    }

    this.initSlider();
    window.addEventListener('on:debounced-resize', this.breakpointChangeHandler);
  }

  initSlider() {
    if (!(this.getAttribute('disable-mobile') && !window.matchMedia(theme.mediaQueries.sm).matches)
      && !(this.getAttribute('disable-desktop') && window.matchMedia(theme.mediaQueries.sm).matches)) {
      this.gridWidth = this.grid.clientWidth;

      // Distance between leading edges of adjacent slides (i.e. width of card + gap).
      this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

      // Width of gap between slides.
      this.slideGap = this.slideSpan - this.slides[0].clientWidth;

      this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
      this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
      this.totalPages = this.slides.length - this.slidesPerPage + 1;

      this.setCarouselState(this.totalPages > 1);
      if (this.totalPages < 2) return;

      this.sliderStart = this.getWindowOffset(this.slider);
      if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
      this.sliderEnd = this.sliderStart + this.gridWidth;

      if (window.matchMedia('(pointer: fine)').matches) {
        this.slider.classList.add('is-grabbable');
      }

      this.addListeners();
      this.setButtonStates();
    } else {
      this.setAttribute('inactive', '');
    }

    // The native scroller works immediately; the enhanced scrollbar is an
    // optional asset loaded only once this carousel approaches the viewport.
    initOverlayScrollbar(this.slider);
  }

  addListeners() {
    if (this.nav) {
      this.scrollHandler = debounce(this.handleScroll.bind(this));
      this.navClickHandler = this.handleNavClick.bind(this);

      this.slider.addEventListener('scroll', this.scrollHandler);
      this.nav.addEventListener('click', this.navClickHandler);
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      this.mousedownHandler = this.handleMousedown.bind(this);
      this.mouseupHandler = this.handleMouseup.bind(this);
      this.mousemoveHandler = this.handleMousemove.bind(this);

      this.slider.addEventListener('mousedown', this.mousedownHandler);
      this.slider.addEventListener('mouseup', this.mouseupHandler);
      this.slider.addEventListener('mouseleave', this.mouseupHandler);
      this.slider.addEventListener('mousemove', this.mousemoveHandler);
    }
  }

  removeListeners() {
    if (this.nav) {
      this.slider.removeEventListener('scroll', this.scrollHandler);
      this.nav.removeEventListener('click', this.navClickHandler);
    }

    this.slider.removeEventListener('mousedown', this.mousedownHandler);
    this.slider.removeEventListener('mouseup', this.mouseupHandler);
    this.slider.removeEventListener('mouseleave', this.mouseupHandler);
    this.slider.removeEventListener('mousemove', this.mousemoveHandler);
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
    this.setButtonStates();
  }

  /**
   * Handles 'mousedown' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.sliderStart;
    this.scrollPos = this.slider.scrollLeft;
    this.slider.classList.add('is-grabbing');
  }

  /**
   * Handles 'mouseup' events on the slider element.
   */
  handleMouseup() {
    this.mousedown = false;
    this.slider.classList.remove('is-grabbing');
    this.slider.classList.remove('is-dragging');
  }

  /**
   * Handles 'mousemove' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.sliderStart;
    this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
    this.slider.classList.add('is-dragging');
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches('.slider-nav__btn')) return;

    if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
      this.scrollPos = this.slider.scrollLeft + this.slidesToScroll * this.slideSpan;
    } else {
      this.scrollPos = this.slider.scrollLeft - this.slidesToScroll * this.slideSpan;
    }

    this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
  }

  /**
   * Handles 'on:debounced-resize' events on the window.
   */
  handleBreakpointChange() {
    this.removeListeners();
    this.initSlider();
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    return this.rtl
      ? window.innerWidth - el.getBoundingClientRect().right
      : el.getBoundingClientRect().left;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   */
  setCarouselState(active) {
    if (active) {
      this.removeAttribute('inactive');

      // If slider width changed when activated, reinitialise it.
      if (this.gridWidth !== this.grid.clientWidth) {
        this.handleBreakpointChange();
      }
    } else {
      this.setAttribute('inactive', '');
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    if (!this.prevBtn && !this.nextBtn) {
      return;
    }

    this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);
  }
}

customElements.define('carousel-slider', CarouselSlider);

// OverlayScrollbars is loaded on demand when a carousel approaches the viewport.

/**
 * Prépare les dépendances d'un fragment de cartes avant son insertion puis
 * active ses scripts. Les facettes, la pagination et les rails asynchrones
 * partagent cette API explicite afin d'éviter un MutationObserver global.
 */
(() => {
  const styleLoads = new Map();
  const styleLinkStates = new WeakMap();
  const unusableStyleLinks = new WeakSet();
  const styleTimeout = 1500;
  const hasMatch = (root, selector) => Boolean(
    root
    && (root.matches?.(selector) || root.querySelector?.(selector))
  );

  const loadStyle = (url, anchorSelector = '[data-dg-dynamic-style-anchor]') => {
    if (!url) return Promise.resolve(false);

    const href = new URL(url, document.baseURI).href;
    const cached = styleLoads.get(href);
    const cachedState = cached?.link ? styleLinkStates.get(cached.link) : null;
    if (
      cached?.link?.isConnected
      && (cachedState?.status === 'loading' || cachedState?.status === 'loaded')
    ) {
      return cached.promise;
    }
    if (cached) styleLoads.delete(href);

    let link = Array.from(document.querySelectorAll('link[rel~="stylesheet"]'))
      .find((item) => item.href === href && !unusableStyleLinks.has(item));
    let owned = false;

    const setLinkState = (state, status) => {
      state.status = status;
      if (state.owned) state.link.dataset.dgDynamicProductStyleState = status;
    };

    const trackLink = (item, isOwned) => {
      const tracked = styleLinkStates.get(item);
      if (tracked) return tracked;

      const state = {
        link: item,
        href,
        owned: isOwned,
        status: item.sheet ? 'loaded' : 'loading'
      };
      styleLinkStates.set(item, state);
      if (isOwned) item.dataset.dgDynamicProductStyleState = state.status;

      if (state.status === 'loading') {
        item.addEventListener('load', () => {
          setLinkState(state, 'loaded');
          unusableStyleLinks.delete(item);
          item.media = 'all';
          item.disabled = false;
        }, { once: true });
        item.addEventListener('error', () => {
          setLinkState(state, 'error');
          unusableStyleLinks.add(item);
          if (styleLoads.get(href)?.link === item) styleLoads.delete(href);
          // Les liens critiques/différés appartiennent au layout. On les
          // ignore après erreur, mais seul un lien créé ici peut être retiré.
          if (state.owned && item.isConnected) item.remove();
        }, { once: true });
      }

      return state;
    };

    let state = link ? trackLink(
      link,
      link.dataset.dgDynamicProductStyle !== undefined
    ) : null;

    // Un premier fragment a déjà attendu son budget maximal. Si la requête
    // n'a toujours ni chargé ni échoué, le prochain fragment mérite un vrai
    // retry. Le lien du layout reste intact ; un ancien lien dynamique peut
    // être remplacé puisqu'il nous appartient.
    if (state?.status === 'timedout' || state?.status === 'error') {
      unusableStyleLinks.add(link);
      if (state.owned && link.isConnected) link.remove();
      link = null;
      state = null;
    }

    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.media = 'all';
      link.dataset.dgDynamicProductStyle = '';
      owned = true;
      state = trackLink(link, true);
    }

    // Une feuille différée déjà découverte doit s'appliquer dès que le
    // fragment qui en dépend va être rendu.
    link.media = 'all';
    link.disabled = false;

    if (link.sheet || state?.status === 'loaded') {
      if (state) setLinkState(state, 'loaded');
      return Promise.resolve(true);
    }

    const promise = new Promise((resolve) => {
      let settled = false;
      let timerId;

      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timerId);
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
        if (!loaded && styleLoads.get(href)?.link === link) styleLoads.delete(href);
        resolve(loaded);
      };

      const onLoad = () => finish(true);
      const onError = () => finish(false);
      link.addEventListener('load', onLoad, { once: true });
      link.addEventListener('error', onError, { once: true });

      // Après ce délai, le contenu conserve son rendu de repli. Une feuille
      // simplement lente reste toutefois en réseau. Son listener permanent
      // enregistrera un load tardif ; sinon le prochain fragment la retentera.
      timerId = window.setTimeout(() => {
        if (settled) return;
        if (state?.status === 'loading') setLinkState(state, 'timedout');
        finish(false);
      }, styleTimeout);
    });

    styleLoads.set(href, { link, promise });
    if (owned) {
      const anchor = document.querySelector(anchorSelector);
      if (anchor) {
        anchor.before(link);
      } else {
        document.head.appendChild(link);
      }
    }
    return promise;
  };

  // Les composants AJAX hors grilles (par exemple le panier qui devient vide)
  // réutilisent exactement le même registre, le même timeout et la même
  // politique de retry, sans embarquer un second chargeur de feuilles.
  window.dgLoadThemeStyleReady = loadStyle;

  document.addEventListener('dg:dynamic-product-content:prepare', (evt) => {
    const { root, waitUntil } = evt.detail || {};
    if (typeof waitUntil !== 'function') return;

    // L'ordre reproduit le head historique : deal puis géométrie des cartes.
    if (hasMatch(root, '[data-daily-deal-countdown]')) {
      waitUntil(loadStyle(
        window.theme?.assets?.dailyDealStyles,
        '[data-dg-daily-deal-style-anchor]'
      ));
    }
    if (hasMatch(root, 'product-card')) {
      waitUntil(loadStyle(window.theme?.assets?.productCardStyles));
    }
    if (hasMatch(root, '.empty-state')) {
      waitUntil(loadStyle(window.theme?.assets?.emptyStateStyles));
    }
  });

  document.addEventListener('dg:dynamic-product-content:inserted', (evt) => {
    const root = evt.detail?.root;
    if (hasMatch(root, '[data-daily-deal-countdown]')) {
      window.dgLoadThemeScript?.(window.theme?.assets?.dailyDeal);
    }
    if (hasMatch(root, 'product-card')) {
      window.dgLoadThemeScript?.(window.theme?.assets?.productCardPeek);
    }
  });

  window.dgPrepareDynamicProductContent = (root) => {
    const waits = [];
    document.dispatchEvent(new CustomEvent('dg:dynamic-product-content:prepare', {
      detail: {
        root,
        waitUntil: (promise) => waits.push(Promise.resolve(promise))
      }
    }));
    return Promise.allSettled(waits);
  };

  window.dgActivateDynamicProductContent = (root) => {
    document.dispatchEvent(new CustomEvent('dg:dynamic-product-content:inserted', {
      detail: { root }
    }));
  };

  document.addEventListener('shopify:section:load', (evt) => {
    window.dgPrepareDynamicProductContent(evt.target)
      .then(() => window.dgActivateDynamicProductContent(evt.target));
  });

  window.addEventListener('pageshow', (evt) => {
    if (evt.persisted) {
      window.dgPrepareDynamicProductContent(document)
        .then(() => window.dgActivateDynamicProductContent(document));
    }
  });
})();

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    if (this._loaded === undefined) this._loaded = false;

    const hiddenWrapper = this.closest('.product-info__block[hidden]');
    const shouldInitImmediately = Boolean(window.Shopify?.designMode || hiddenWrapper);

    if (shouldInitImmediately) {
      this.init();
    } else {
      this._intersectionObserver = initLazyScript(this, this.init.bind(this), 600);
    }
  }

  disconnectedCallback() {
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = null;
    if (this._requestController) {
      this._requestController.abort();
      this._requestController = null;
    }
    // Une même instance peut être déplacée puis reconnectée par l'éditeur,
    // y compris avant que l'IntersectionObserver ait lancé le fetch.
    if (!this._loaded) this._initialized = false;
  }

  async init() {
    const { productId } = this.dataset;
    if (!productId || this._requestController) return;

    const wrapper = this.closest('.product-info__block');
    const requestController = new AbortController();
    this._requestController = requestController;

    try {
      const response = await fetch(`${this.dataset.url}&product_id=${productId}`, {
        signal: requestController.signal
      });
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      if (requestController.signal.aborted || !this.isConnected) return;

      const el = tmpl.content.querySelector('product-recommendations');
      if (el && el.hasChildNodes()) {
        await window.dgPrepareDynamicProductContent?.(el);
        if (requestController.signal.aborted || !this.isConnected) return;
        this.innerHTML = el.innerHTML;
        window.dgActivateDynamicProductContent?.(this);
        if (wrapper) wrapper.hidden = false;
      } else if (wrapper) {
        wrapper.hidden = true;
      }
      this._loaded = true;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (wrapper) wrapper.hidden = true;
      console.log(error); // eslint-disable-line
    } finally {
      if (this._requestController === requestController) {
        this._requestController = null;
      }
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

window.initLazyScript = initLazyScript;

document.addEventListener('keydown', (evt) => {
  if (evt.code === 'Tab') {
    document.body.classList.add('tab-used');
  }
});

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    document.body.classList.add('dom-loaded');
  }, 0);

  setTimeout(() => {
    document.body.classList.add('dom-loaded-plus-6');
  }, 6000);

  if (theme.settings.externalLinksNewTab) {
    document.addEventListener('click', (evt) => {
      const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
      if (link && link.tagName === 'A' && link.href && window.location.hostname !== new URL(link.href).hostname) {
        const isLoginLink = (
          link.href
          && theme.routes.accountLoginUrl
          && new URL(link.href).hostname === new URL(theme.routes.accountLoginUrl, window.location.origin).hostname
        );
        if (!isLoginLink) link.target = '_blank';
      }
    });
  }

  // Ensure anchor scrolling is smooth (this shouldn't be added in the CSS)
  document.addEventListener('click', (evt) => {
    if (evt.target.tagName === 'A' && window.location.hostname === new URL(evt.target.href).hostname
      && evt.target.href.includes('#')) {
      document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
      setTimeout(() => {
        document.getElementsByTagName('html')[0].style.scrollBehavior = '';
      }, 1000);
    }
  });
});

// Emit an event to indicate the page was restored from bfcache, https://web.dev/articles/bfcache
window.addEventListener('pageshow', (evt) => {
  if (evt.persisted) {
    document.dispatchEvent(new CustomEvent('on:bfcache:load-restore'));
  } else {
    document.dispatchEvent(new CustomEvent('on:bfcache:load-normal'));
  }
});

// Init mobile-only scrolling near the viewport. Re-running this for a section
// injected by the editor is safe because initOverlayScrollbar is idempotent.
const initMobileScrollbars = (root = document) => {
  root.querySelectorAll('.slider--mobile').forEach((slider) => {
    initLazyScript(slider, () => initOverlayScrollbar(slider), 500);
  });
};

initMobileScrollbars();
document.addEventListener('shopify:section:load', (evt) => initMobileScrollbars(evt.target));
